import React from 'react';
import { useApi } from '../../api/useApi';
import { hostDashboard, listProperties, listBookings } from '../../api/hostApi';
import StatCard from '../../components/shared/StatCard';
import PageHeader from '../../components/shared/PageHeader';
import { CalendarDays, DollarSign, Home, MessageSquare, Plus, CreditCard, ArrowRight, Star } from 'lucide-react';
import { StatusPill, Card } from '../../components/shared/DataToolbar';
import { Link } from 'react-router-dom';
import { LoadingState, ErrorState } from '../../components/shared/Pending';
import { useAuth } from '../../contexts/AuthContext';

const imageUrl = (property) => property?.cover_image?.url || property?.cover_image || property?.coverImage || 'https://placehold.co/600x360';
const rows = (value) => Array.isArray(value) ? value : (value?.items || value?.data || []);

export default function HostDashboard() {
  const { user } = useAuth();
  const { data: dash, loading, error, refetch } = useApi(hostDashboard, []);
  const { data: propsData, loading: propertiesLoading } = useApi(() => listProperties({ per_page: 6 }), []);
  const { data: bookingsData, loading: bookingsLoading } = useApi(() => listBookings({ per_page: 6 }), []);

  if (loading) return <LoadingState label="Loading your dashboard…" />;
  if (error) return <ErrorState error={error} onRetry={refetch} />;

  const stats = dash?.stats || {};
  const properties = rows(propsData);
  const bookings = rows(bookingsData);
  const upcoming = bookings.filter((booking) => ['confirmed', 'paid'].includes(booking.status));
  const firstUpcoming = upcoming[0];
  const firstName = user?.name?.split(' ')?.[0];

  return (
    <div>
      <PageHeader
        title={firstName ? `Welcome back, ${firstName}` : 'Host dashboard'}
        description="Bookings, properties, messages and payouts that need your attention."
        action={
          <Link to="/host/properties/new" className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-800">
            <Plus className="h-4 w-4" /> Add property
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Confirmed revenue" value={fmt(stats.total_revenue)} icon={DollarSign} tone="teal" />
        <StatCard label="Active properties" value={stats.active_properties ?? 0} icon={Home} tone="sky" />
        <StatCard label="Upcoming stays" value={stats.upcoming_stays ?? upcoming.length} icon={CalendarDays} tone="amber" />
        <StatCard label="Unread messages" value={stats.unread_messages ?? 0} icon={MessageSquare} tone="slate" />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.4fr_0.8fr]">
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-900">Upcoming and recent bookings</h3>
              <p className="mt-0.5 text-xs text-slate-500">The reservations most likely to need attention next.</p>
            </div>
            <Link to="/host/bookings" className="text-xs font-medium text-teal-700 hover:underline">View all</Link>
          </div>

          {bookingsLoading ? (
            <RowsSkeleton rows={5} />
          ) : bookings.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center">
              <CalendarDays className="mx-auto h-6 w-6 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">No bookings yet</p>
              <p className="mt-1 text-xs text-slate-500">Confirmed guest reservations will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {bookings.slice(0, 6).map((booking) => (
                <Link key={booking.id} to={`/host/bookings/${booking.id}`} className="flex items-center gap-3 py-3 transition-colors hover:bg-slate-50/70">
                  <img src={imageUrl(booking.property)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-slate-900">{booking.property?.title || 'Property'}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{booking.check_in || booking.checkIn || '—'} → {booking.check_out || booking.checkOut || '—'} · {booking.guests || 1} guest{Number(booking.guests || 1) === 1 ? '' : 's'}</div>
                    {booking.guest?.name || booking.guest_name ? <div className="mt-0.5 truncate text-xs text-slate-400">Primary guest: {booking.guest?.name || booking.guest_name}</div> : null}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900">{fmt(booking.total_amount ?? booking.total)}</div>
                    <div className="mt-1"><StatusPill status={booking.status} /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          {firstUpcoming && (
            <Card className="overflow-hidden">
              <img src={imageUrl(firstUpcoming.property)} alt="" className="h-36 w-full object-cover" />
              <div className="p-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-700">Next reservation</p>
                <h3 className="mt-2 truncate text-base font-semibold text-slate-900">{firstUpcoming.property?.title || 'Upcoming stay'}</h3>
                <p className="mt-1 text-xs text-slate-500">{firstUpcoming.check_in || firstUpcoming.checkIn} → {firstUpcoming.check_out || firstUpcoming.checkOut}</p>
                <Link to={`/host/bookings/${firstUpcoming.id}`} className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:underline">Open booking <ArrowRight className="h-3.5 w-3.5" /></Link>
              </div>
            </Card>
          )}

          <Card className="p-6">
            <h3 className="font-semibold text-slate-900">Account & payout status</h3>
            <p className="mt-0.5 text-xs text-slate-500">Only the operational setup required to receive bookings and payouts.</p>
            <div className="mt-5 space-y-3 text-sm">
              <StatusRow label="Subscription" value={user?.subscription?.status || '—'} />
              <StatusRow label="Plan" value={user?.subscription?.plan || '—'} />
              <StatusRow label="Avg rating" value={stats.avg_rating ?? '—'} />
            </div>
            <div className="mt-5 grid gap-2">
              <Link to="/host/payout-setup" className="inline-flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-medium text-slate-700 hover:border-teal-300 hover:text-teal-700"><span className="inline-flex items-center gap-2"><CreditCard className="h-3.5 w-3.5" /> Payout setup</span><ArrowRight className="h-3.5 w-3.5" /></Link>
              <Link to="/host/messages" className="inline-flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5 text-xs font-medium text-slate-700 hover:border-teal-300 hover:text-teal-700"><span className="inline-flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5" /> Guest messages</span><ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </Card>
        </div>
      </div>

      <Card className="mt-6 p-6">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-slate-900">Properties</h3>
            <p className="mt-0.5 text-xs text-slate-500">Current listing status and nightly price.</p>
          </div>
          <Link to="/host/properties" className="text-xs font-medium text-teal-700 hover:underline">Manage properties</Link>
        </div>

        {propertiesLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-60 animate-pulse rounded-xl bg-slate-100" />)}</div>
        ) : properties.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center">
            <Home className="mx-auto h-6 w-6 text-slate-300" />
            <p className="mt-3 text-sm font-medium text-slate-700">No properties yet</p>
            <Link to="/host/properties/new" className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 hover:underline"><Plus className="h-3.5 w-3.5" /> Add your first property</Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {properties.slice(0, 6).map((property) => (
              <Link key={property.id} to={`/host/properties/${property.id}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-sm">
                <div className="aspect-[16/10] overflow-hidden"><img src={imageUrl(property)} alt="" className="h-full w-full object-cover" /></div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0"><div className="truncate font-medium text-slate-900">{property.title}</div><div className="truncate text-xs text-slate-500">{property.location || [property.location_details?.city, property.location_details?.state].filter(Boolean).join(', ')}</div></div>
                    <StatusPill status={property.status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <div className="font-semibold tabular-nums text-slate-900">{fmt(property.price_per_night ?? property.pricePerNight)}<span className="ml-0.5 text-xs font-normal text-slate-500">/night</span></div>
                    <div className="flex items-center gap-1 text-xs text-slate-500"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {property.average_rating ?? property.rating ?? '—'}<span className="text-slate-400">({property.review_count ?? property.reviews_count ?? 0})</span></div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const StatusRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4"><span className="text-slate-500">{label}</span><span className="font-semibold capitalize text-slate-900">{value}</span></div>
);

const RowsSkeleton = ({ rows = 5 }) => (
  <div className="divide-y divide-slate-100 animate-pulse">
    {Array.from({ length: rows }).map((_, index) => <div key={index} className="flex items-center gap-3 py-3"><div className="h-12 w-12 rounded-lg bg-slate-100" /><div className="flex-1"><div className="h-3 w-2/5 rounded bg-slate-100" /><div className="mt-2 h-2.5 w-3/5 rounded bg-slate-100" /></div><div className="h-8 w-16 rounded bg-slate-100" /></div>)}
  </div>
);

function fmt(value) {
  if (value == null || value === '') return '—';
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return `$${number.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
